import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useQuery } from "react-query";
import { CONTROL_STEP_OPTIONS } from "./constants";
import ActiveControlsRow from "./row";
import useLang from "Shared/hooks/useLanguage";
import { sortById, SortingTypeI } from "Utils/consts";
import { Loader } from "Shared/Common/Loader";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import usePagination from "Shared/hooks/usePagination";
import { BatchControlsGetAll } from "Services/BatchControls/BatchControls";
import NoRecord from "Shared/Common/NoRecord";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";

const initialSearchCriteria = {
  controlName: "",
  controlForRunStep: "",
  controlType: "",
};

const FILTER_FIELD_LABELS: Record<keyof typeof initialSearchCriteria, string> =
  {
    controlName: "Controls Name",
    controlForRunStep: "Control For Run Step",
    controlType: "Controls Type",
  };

const initialPostData = {
  batchControlId: 0,
  controlName: "",
  controlType: "",
  ControlForRunStep: "",
};

const ActiveControls: React.FC = () => {
  const { t } = useLang();
  const [triggerSearch, setTriggerSearch] = useState(false);
  const [sortTrigger, setSortTrigger] = useState(false);
  const [localRows, setLocalRows] = useState<any[]>([]);
  const [, setIsBtnDisabled] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(initialSearchCriteria);
  const [reset, setReset] = useState(false);
  const [sort, setSorting] = useState<SortingTypeI>(sortById);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);

  const {
    total,
    curPage,
    showPage,
    nextPage,
    prevPage,
    setTotal,
    pageSize,
    totalPages,
    pageNumbers,
    setPageSize,
    setCurPage,
  } = usePagination();

  const handleSort = (col: string) => {
    const newOrder =
      sort.clickedIconData === col
        ? sort.sortingOrder === "asc"
          ? "desc"
          : "asc"
        : "asc";
    setSorting({ sortingOrder: newOrder, clickedIconData: col });
    setSortTrigger((p) => !p);
  };

  const queryPayload = {
    pageNumber: curPage,
    pageSize,
    queryModel: {
      controlName: searchCriteria.controlName || "",
      // ControlForRunStep: searchCriteria.controlForRunStep
      //   ? Number(searchCriteria.controlForRunStep)
      //   : null,
      controlForRunStep: searchCriteria.controlForRunStep || "",
      controlType: searchCriteria.controlType || "",
      isActive: true,
    },
    sortColumn: sort.clickedIconData || "Id",
    sortDirection: sort.sortingOrder || "Desc",
  };

  const {
    data: queryResult,
    isFetching: loading,
    refetch,
  } = useQuery(
    ["controls-active", curPage, pageSize, triggerSearch, sortTrigger, reset],
    () => BatchControlsGetAll(queryPayload),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: (resp: any) => setTotal(resp?.data?.total ?? 0),
      onError: () => console.error("Error fetching controls data"),
    }
  );

  const apiRows: any[] = queryResult?.data?.data ?? [];
  const localRowIds = new Set(localRows.map((r) => r.batchControlId));
  const filteredApiRows = apiRows.filter(
    (r) => !localRowIds.has(r.batchControlId)
  );
  const tableData = [...localRows, ...filteredApiRows];

  const handleSearch = () => {
    setCurPage(1);
    setTriggerSearch((p) => !p);
  };

  const handleReset = () => {
    setSearchCriteria(initialSearchCriteria);
    setSorting(sortById);
    setCurPage(1);
    setReset((p) => !p);
  };

  useEffect(() => {
    const tags = Object.entries(searchCriteria)
      .filter(
        ([, value]) => value !== null && value !== undefined && value !== ""
      )
      .map(([key, value]) => {
        const label =
          FILTER_FIELD_LABELS[key as keyof typeof initialSearchCriteria];

        if (key === "controlForRunStep") {
          const stepLabel =
            CONTROL_STEP_OPTIONS.find((option) => option.value === value)
              ?.label || value;

          return `${label}: ${stepLabel}`;
        }

        return `${label}: ${value}`;
      });

    setSearchedTags(tags);
  }, [searchCriteria]);
  const handleTagRemoval = (tag: string) => {
    const matchedField = Object.entries(FILTER_FIELD_LABELS).find(([, label]) =>
      tag.startsWith(`${label}:`)
    )?.[0] as keyof typeof initialSearchCriteria | undefined;

    if (!matchedField) return;

    setSearchCriteria((prev) => ({
      ...prev,
      [matchedField]: "",
    }));

    setCurPage(1);
    setTriggerSearch((p) => !p);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const sortIcons = (col: string) => ({
    up: `${
      sort.sortingOrder === "desc" && sort.clickedIconData === col
        ? "text-success fs-7"
        : "text-gray-700 fs-7"
    } p-0 m-0`,
    down: `${
      sort.sortingOrder === "asc" && sort.clickedIconData === col
        ? "text-success fs-7"
        : "text-gray-700 fs-7"
    } p-0 m-0`,
  });

  return (
    <>
      {/* Toolbar */}
      <div className="d-flex flex-wrap justify-content-center justify-content-sm-between align-items-center responsive-flexed-actions mb-2 gap-2">
        <div className="d-flex align-items-center responsive-flexed-actions gap-2">
          <div className="d-flex align-items-center">
            <span className="fw-400 mr-2">{t("Records")}</span>
            <select
              id="ActiveControlsRecords"
              className="form-select w-100px h-33px rounded py-2"
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurPage(1);
              }}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <PermissionComponent
            moduleName="Oncology LIS"
            pageName="Controls"
            permissionIdentifier="AddNew"
          >
            {({ pageName, moduleName, permissionIdentifier }) => (
              <button
                id="ActiveControlsAddNew"
                className="btn btn-primary btn-sm btn-primary--icon px-7"
                disabled={loading}
                onClick={() =>
                  setLocalRows((prev) => [
                    { rowStatus: true, ...initialPostData },
                    ...prev,
                  ])
                }
              >
                <i className="fa-solid fa-plus"></i>
                <span style={{ fontSize: 11 }}>
                  {getPermissionDisplayName(
                    pageName,
                    moduleName,
                    permissionIdentifier,
                    "Create Control"
                  )}
                </span>
              </button>
            )}
          </PermissionComponent>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            id="ActiveControlsSearch"
            className="btn btn-linkedin btn-sm fw-500"
            onClick={handleSearch}
          >
            {t("Search")}
          </button>
          <button
            id="ActiveControlsReset"
            className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
            onClick={handleReset}
          >
            <span>{t("Reset")}</span>
          </button>
        </div>
      </div>

      <div className="d-flex gap-2 flex-wrap pb-2">
        {searchedTags.map((tag) => (
          <div
            key={tag}
            className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
            onClick={() => handleTagRemoval(tag)}
          >
            <span className="fw-bold">{tag}</span>
            <i className="bi bi-x"></i>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="card">
        <Box sx={{ height: "auto", width: "100%" }}>
          <div className="table_bordered overflow-hidden">
            <TableContainer
              sx={{
                maxHeight: "calc(100vh - 100px)",
                "&::-webkit-scrollbar": { width: 7 },
                "&::-webkit-scrollbar-track": { backgroundColor: "#fff" },
                "&:hover": {
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "var(--kt-gray-400)",
                    borderRadius: 2,
                  },
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--kt-gray-400)",
                  borderRadius: 2,
                },
              }}
              component={Paper}
              className="shadow-none"
            >
              <Table
                aria-label="active controls table"
                className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
              >
                <TableHead>
                  {/* Search row */}
                  <TableRow className="h-40px">
                    <TableCell className="w-50px" />
                    <TableCell style={{ minWidth: "200px" }}>
                      <input
                        type="text"
                        className="form-control bg-white min-w-150px w-100 rounded-2 fs-8 h-30px"
                        placeholder="Search..."
                        value={searchCriteria.controlName}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          setSearchCriteria((p) => ({
                            ...p,
                            controlName: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "220px" }}>
                      <select
                        className="form-select bg-white w-100 rounded-2 fs-8 h-30px py-0"
                        value={searchCriteria.controlForRunStep}
                        onChange={(e) =>
                          setSearchCriteria((p) => ({
                            ...p,
                            controlForRunStep: e.target.value,
                          }))
                        }
                      >
                        <option value="">--- Select ---</option>
                        {CONTROL_STEP_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <select
                        className="form-select bg-white w-100 rounded-2 fs-8 h-30px py-0"
                        value={searchCriteria.controlType}
                        onChange={(e) =>
                          setSearchCriteria((p) => ({
                            ...p,
                            controlType: e.target.value,
                          }))
                        }
                      >
                        <option value="">--- Select ---</option>
                        <option value="Positive">Positive</option>
                        <option value="Negative">Negative</option>
                      </select>
                    </TableCell>
                  </TableRow>

                  {/* Column headers */}
                  <TableRow className="h-30px">
                    <TableCell>{t("Actions")}</TableCell>
                    <TableCell style={{ minWidth: "200px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("controlName")}
                      >
                        <span style={{ width: "max-content" }}>
                          {t("Controls Name")}
                        </span>
                        <div className="d-flex align-items-center mx-4 mr-0">
                          <ArrowUp CustomeClass={sortIcons("controlName").up} />
                          <ArrowDown
                            CustomeClass={sortIcons("controlName").down}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("controlForRunStep")}
                      >
                        <span style={{ width: "max-content" }}>
                          {t("Control For Run Step")}
                        </span>
                        <div className="d-flex align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={sortIcons("controlForRunStep").up}
                          />
                          <ArrowDown
                            CustomeClass={sortIcons("controlForRunStep").down}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("controlType")}
                      >
                        <span style={{ width: "max-content" }}>
                          {t("Controls Type")}
                        </span>
                        <div className="d-flex align-items-center mx-4 mr-0">
                          <ArrowUp CustomeClass={sortIcons("controlType").up} />
                          <ArrowDown
                            CustomeClass={sortIcons("controlType").down}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : tableData.length ? (
                    tableData.map((row: any, index: number) => (
                      <ActiveControlsRow
                        key={row.batchControlId ?? `new-${index}`}
                        row={row}
                        index={index}
                        setLocalRows={setLocalRows}
                        refetch={refetch}
                        setIsBtnDisabled={setIsBtnDisabled}
                      />
                    ))
                  ) : (
                    <NoRecord colSpan={4} />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </div>

      {/* Pagination */}
      <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mt-4">
        <p className="pagination-total-record mb-0">
          <span>
            {t("Showing")} {pageSize * (curPage - 1) + 1} {t("to")}{" "}
            {Math.min(pageSize * curPage, total)} {t("of Total")}
            <span> {total} </span>
            {t("entries")}
          </span>
        </p>
        <ul className="d-flex align-items-center justify-content-end custome-pagination p-0 mb-0">
          <li className="btn btn-lg p-2 h-33px" onClick={() => showPage(1)}>
            <i className="fa fa-angle-double-left" />
          </li>
          <li className="btn btn-lg p-2 h-33px" onClick={prevPage}>
            <i className="fa fa-angle-left" />
          </li>
          {pageNumbers.map((page) => (
            <li
              key={page}
              className={`px-2 ${
                page === curPage
                  ? "font-weight-bold bg-primary text-white h-33px"
                  : ""
              }`}
              style={{ cursor: "pointer" }}
              onClick={() => showPage(page)}
            >
              {page}
            </li>
          ))}
          <li className="btn btn-lg p-2 h-33px" onClick={nextPage}>
            <i className="fa fa-angle-right" />
          </li>
          <li
            className="btn btn-lg p-2 h-33px"
            onClick={() => showPage(totalPages === 0 ? curPage : totalPages)}
          >
            <i className="fa fa-angle-double-right" />
          </li>
        </ul>
      </div>
    </>
  );
};

export default ActiveControls;
